// Demo creative generation, rendered locally. In production this is
// Higgsfield's generation pipeline; the portal contract is only "a finished
// image file plus an hfa_ asset id".

export type CreativeFormat = "1:1" | "9:16" | "16:9";

export type HiggsfieldAsset = {
  id: string;
  prompt: string | null;
  format: string | null;
  filename: string;
  whop_file_id: string | null;
  linked_at: string | null;
  created_at: string;
};

export const CREATIVE_DIMENSIONS: Record<CreativeFormat, [number, number]> = { "1:1": [800, 800], "9:16": [720, 1280], "16:9": [1280, 720] };
const LIME = "#DFFF29";

export function renderCreative(prompt: string, format: CreativeFormat, seed: number): Promise<{ blob: Blob; dataUrl: string }> {
  const [width, height] = CREATIVE_DIMENSIONS[format];
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("Canvas is unavailable in this browser."));
  const random = (index: number) => {
    const value = Math.sin(seed * 999 + index * 131.71) * 10_000;
    return value - Math.floor(value);
  };
  context.fillStyle = "#0A0A0A";
  context.fillRect(0, 0, width, height);
  for (let index = 0; index < 4; index += 1) {
    context.save();
    context.translate(random(index) * width, random(index + 9) * height);
    context.rotate((random(index + 4) - 0.5) * 1.6);
    context.fillStyle = index % 2 ? "rgba(223,255,41,.16)" : "rgba(223,255,41,.34)";
    const size = (0.25 + random(index + 7) * 0.4) * Math.min(width, height);
    if (index % 3 === 0) {
      context.beginPath();
      context.arc(0, 0, size / 2, 0, Math.PI * 2);
      context.fill();
    } else {
      context.beginPath();
      context.roundRect(-size / 2, -size / 6, size, size / 3, size / 6);
      context.fill();
    }
    context.restore();
  }
  const margin = Math.round(width * 0.08);
  const fontSize = Math.round(Math.min(width, height) / 10);
  context.font = `700 ${fontSize}px "Space Grotesk", Inter, sans-serif`;
  context.textBaseline = "top";
  const words = (prompt.trim() || "Your product, everywhere").toUpperCase().split(/\s+/).slice(0, 12);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (context.measureText(next).width > width - margin * 2 && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  const textTop = height - margin - (lines.length + 1) * (fontSize * 1.12) - fontSize * 0.8;
  lines.slice(0, 4).forEach((text, index) => {
    context.fillStyle = index === lines.length - 1 ? LIME : "#F3F3F1";
    context.fillText(text, margin, textTop + index * fontSize * 1.12);
  });
  context.font = `600 ${Math.round(fontSize * 0.42)}px "Space Grotesk", Inter, sans-serif`;
  context.fillStyle = "rgba(243,243,241,.6)";
  context.fillText("HIGGSFIELD ADS", margin, height - margin - fontSize * 0.42);
  const dataUrl = canvas.toDataURL("image/png");
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve({ blob, dataUrl }) : reject(new Error("The creative could not be rendered."))), "image/png");
  });
}
